import React, { useState } from "react";
import {
  Wand2,
  Loader2,
  Sparkles,
  FileText,
  Camera,
  ChevronRight,
  CheckCircle,
  Clock,
  Download,
  Copy,
  Share2,
  Eye,
  FolderDown,
  Upload,
  Link,
  Settings,
} from "lucide-react";
import ApiKeysModal from './ApiKeysModal';

const KickstarterContentGenerator = () => {
  const [productInfo, setProductInfo] = useState({
    name: "",
    category: "",
    description: "",
    targetAudience: "",
    fundingGoal: "",
    keyProblem: "",
  });

  const [generatedContent, setGeneratedContent] = useState(null);
  const [currentStep, setCurrentStep] = useState("input");
  const [selectedSection, setSelectedSection] = useState("overview");
  const [generatedImages, setGeneratedImages] = useState({});
  const [imageGenerating, setImageGenerating] = useState({});
  const [editablePrompts, setEditablePrompts] = useState({});
  const [savedProjects, setSavedProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [campaignSettings, setCampaignSettings] = useState({
    launchDate: "",
    emailService: "none",
    emailServiceConfig: "",
    promotionalMessage: "Get early access to our revolutionary product + 50% OFF launch pricing!",
    ctaText: "Notify Me When Available",
    showLeadCapture: true,
    webhookUrl: ""
  });
  const [imageGenerationService, setImageGenerationService] = useState("dalle"); // dalle or templated
  const [generatedWebsiteUrl, setGeneratedWebsiteUrl] = useState(null);
  const [imageSelectionMode, setImageSelectionMode] = useState({}); // Track which mode is active for each section
  const [unsplashResults, setUnsplashResults] = useState({});
  const [unsplashSearching, setUnsplashSearching] = useState({});

  // AI Provider selection state
  const [aiProvider, setAiProvider] = useState("openai"); // Default to OpenAI

  // API Keys management
  const [apiKeys, setApiKeys] = useState(() => {
    const saved = localStorage.getItem('launchpad-api-keys');
    return saved ? JSON.parse(saved) : {
      openai: '',
      anthropic: '',
      templated: ''
    };
  });
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [showKeyValues, setShowKeyValues] = useState({
    openai: false,
    anthropic: false,
    templated: false
  });

  // Configuration for API endpoint
  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    "https://launchpad.cannappy.workers.dev";

  // Generate or retrieve user ID
  const generateUserId = () => {
    // Check if user ID already exists
    let existingUserId = localStorage.getItem('launchpad_user_id');
    if (existingUserId) {
      return existingUserId;
    }

    // Generate new user ID: timestamp + random string
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    const newUserId = `user_${timestamp}_${randomStr}`;

    // Store in localStorage
    localStorage.setItem('launchpad_user_id', newUserId);
    localStorage.setItem('launchpad_user_created', new Date().toISOString());

    console.log('Generated new user ID:', newUserId);
    return newUserId;
  };

  const categories = [
    "Technology",
    "Design",
    "Games",
    "Fashion",
    "Food",
    "Art",
    "Music",
    "Film",
    "Publishing",
    "Sports",
  ];

  // Initialize user ID and load saved projects
  React.useEffect(() => {
    // Generate or retrieve user ID
    const currentUserId = generateUserId();
    setUserId(currentUserId);

    const loadProjects = async () => {
      try {
        // Try to load from KV storage first
        const response = await fetch(`${API_BASE_URL}/api/projects?userId=${currentUserId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.projects.length > 0) {
            setSavedProjects(data.projects);
            console.log('Loaded projects from KV storage:', data.projects.length);
            return;
          }
        }
      } catch (error) {
        console.warn('Failed to load from KV storage:', error);
      }

      // Fallback to localStorage
      const saved = localStorage.getItem('savedProjects');
      if (saved) {
        try {
          const localProjects = JSON.parse(saved);
          setSavedProjects(localProjects);
          console.log('Loaded projects from localStorage:', localProjects.length);
        } catch (error) {
          console.error('Error loading saved projects:', error);
        }
      }
    };

    loadProjects();
  }, []);

  // Real AI content generation using Cloudflare Worker
  const generateCampaignContent = async () => {
    // Check if user has the required API key for the selected AI provider
    if (aiProvider === "openai" && !apiKeys.openai) {
      alert("Please configure your OpenAI API key first. Click the Settings button in the top right.");
      setShowKeysModal(true);
      return;
    }
    
    if (aiProvider === "anthropic" && !apiKeys.anthropic) {
      alert("Please configure your Anthropic API key first. Click the Settings button in the top right.");
      setShowKeysModal(true);
      return;
    }

    setCurrentStep("generating");

    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productInfo,
          aiProvider,
          userApiKeys: apiKeys
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to generate content");
      }

      console.log("Received data from API:", data);
      console.log("Generated content structure:", {
        hasOverview: !!data.content.overview,
        featuresCount: data.content.features?.length || 0,
        useCasesCount: data.content.useCases?.length || 0,
        rewardsCount: data.content.rewards?.length || 0,
        timelineCount: data.content.timeline?.length || 0,
        faqsCount: data.content.faqs?.length || 0
      });

      setGeneratedContent(data.content);

      // Initialize editable prompts with the generated prompts
      const prompts = {};
      if (data.content.overview?.imagePrompt) {
        prompts.overview = data.content.overview.imagePrompt;
      }
      data.content.features?.forEach(feature => {
        prompts[`feature-${feature.id}`] = feature.imagePrompt;
      });
      data.content.useCases?.forEach(useCase => {
        prompts[`usecase-${useCase.id}`] = useCase.imagePrompt;
      });
      data.content.rewards?.forEach(reward => {
        prompts[`reward-${reward.id}`] = reward.imagePrompt;
      });
      setEditablePrompts(prompts);

      // Save the project to KV storage
      const projectId = Date.now().toString();
      const newProject = {
        id: projectId,
        name: data.content.overview.title,
        productInfo: productInfo,
        content: data.content,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };

      try {
        // Save to KV storage via API
        const saveResponse = await fetch(`${API_BASE_URL}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project: newProject,
            userId: userId || 'anonymous'
          })
        });

        if (saveResponse.ok) {
          console.log('Project saved to KV storage');
        } else {
          console.warn('Failed to save to KV storage, using localStorage fallback');
        }
      } catch (error) {
        console.warn('Error saving to KV storage:', error);
      }

      // Also update local state and localStorage as backup
      const updatedProjects = [newProject, ...savedProjects.slice(0, 9)]; // Keep last 10 projects
      setSavedProjects(updatedProjects);
      setCurrentProjectId(projectId);
      localStorage.setItem('savedProjects', JSON.stringify(updatedProjects));

      setCurrentStep("review");
    } catch (error) {
      console.error("Error generating content:", error);

      let errorMessage = "Failed to generate content. Please try again.";

      if (error.message.includes("529") || error.message.includes("overloaded")) {
        errorMessage = "🤖 Claude AI is currently experiencing high demand. Please wait a moment and try again.";
      } else if (error.message.includes("503") || error.message.includes("503")) {
        errorMessage = "⚡ Service temporarily unavailable. Please try again in a few seconds.";
      } else if (error.message.includes("ANTHROPIC_API_KEY")) {
        errorMessage = "🔑 API configuration issue. Please check your setup.";
      } else if (error.message.includes("network") || error.message.includes("fetch")) {
        errorMessage = "🌐 Network connection issue. Please check your internet and try again.";
      }

      alert(errorMessage);
      setCurrentStep("input");
    }
  };

  const generateImagesForSection = async (section) => {
    // Check if user has the required API key for the selected image generation service
    if (imageGenerationService === "dalle" && !apiKeys.openai) {
      alert("Please configure your OpenAI API key first for DALL-E image generation. Click the Settings button in the top right.");
      setShowKeysModal(true);
      return;
    }
    
    if (imageGenerationService === "templated" && !apiKeys.templated) {
      alert("Please configure your Templated.io API key first. Click the Settings button in the top right.");
      setShowKeysModal(true);
      return;
    }

    setImageGenerating((prev) => ({ ...prev, [section]: true }));

    try {
      // Parse section type and data
      const [sectionType, itemId] = section.includes("-")
        ? section.split("-")
        : [section, null];

      let sectionData;
      let requestData;

      if (sectionType === "overview") {
        sectionData = {
          ...generatedContent.overview,
          imagePrompt: editablePrompts[section] || generatedContent.overview.imagePrompt
        };
        requestData = {
          sectionData: sectionData,
          sectionType: "overview",
          productName: productInfo.name,
        };
      } else if (sectionType === "feature") {
        const originalData = generatedContent.features.find(
          (f) => f.id === parseInt(itemId)
        );
        sectionData = {
          ...originalData,
          imagePrompt: editablePrompts[section] || originalData.imagePrompt
        };
        requestData = {
          sectionData: [sectionData],
          sectionType: "features",
          productName: productInfo.name,
        };
      } else if (sectionType === "usecase") {
        const originalData = generatedContent.useCases.find(
          (u) => u.id === parseInt(itemId)
        );
        sectionData = {
          ...originalData,
          imagePrompt: editablePrompts[section] || originalData.imagePrompt
        };
        requestData = {
          sectionData: [sectionData],
          sectionType: "useCases",
          productName: productInfo.name,
        };
      } else if (sectionType === "reward") {
        const originalData = generatedContent.rewards.find(
          (r) => r.id === parseInt(itemId)
        );
        sectionData = {
          ...originalData,
          imagePrompt: editablePrompts[section] || originalData.imagePrompt
        };
        requestData = {
          sectionData: [sectionData],
          sectionType: "rewards",
          productName: productInfo.name,
        };
      } else {
        // Generate all images for a section type
        requestData = {
          sectionData: generatedContent[sectionType],
          sectionType: sectionType,
          productName: productInfo.name,
        };
      }

      const response = await fetch(
        `${API_BASE_URL}/api/generate-section-images`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...requestData,
            service: imageGenerationService, // Pass the selected service
            userApiKeys: apiKeys, // Pass user's API keys
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to generate images");
      }

      // Store images in R2 and update state with permanent URLs
      const storedImages = await Promise.all(
        data.images.map(async (image) => {
          try {
            const storeResponse = await fetch(`${API_BASE_URL}/api/store-image`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                imageUrl: image.url,
                projectId: currentProjectId || Date.now().toString(),
                sectionType: sectionType,
                itemId: image.id,
              }),
            });

            const storeData = await storeResponse.json();
            if (storeData.success) {
              return {
                ...image,
                url: storeData.storedUrl, // Use R2 URL instead of external URL
                originalUrl: storeData.originalUrl,
                filename: storeData.filename,
              };
            } else {
              console.warn("Failed to store image in R2:", storeData.error);
              return image; // Fallback to original URL
            }
          } catch (error) {
            console.warn("Error storing image in R2:", error);
            return image; // Fallback to original URL
          }
        })
      );

      // Update generated images state
      setGeneratedImages((prev) => ({
        ...prev,
        [section]: storedImages,
      }));
    } catch (error) {
      console.error("Error generating images:", error);
      alert(`Error generating images: ${error.message}`);
    } finally {
      setImageGenerating((prev) => ({ ...prev, [section]: false }));
    }
  };

  const searchUnsplash = async (section, query) => {
    setUnsplashSearching((prev) => ({ ...prev, [section]: true }));

    try {
      const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;

      if (!accessKey || accessKey === 'demo-key') {
        console.log('No Unsplash API key configured, using demo images');
        setUnsplashResults((prev) => ({
          ...prev,
          [section]: generateDemoImages(query)
        }));
        return;
      }

      // Using Unsplash API
      const response = await fetch(
        `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=24&orientation=landscape`,
        {
          headers: {
            'Authorization': `Client-ID ${accessKey}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid Unsplash API key');
        } else if (response.status === 403) {
          throw new Error('Unsplash API rate limit exceeded');
        } else {
          throw new Error(`Unsplash API error: ${response.status}`);
        }
      }

      const data = await response.json();
      setUnsplashResults((prev) => ({
        ...prev,
        [section]: data.results.map(photo => ({
          id: photo.id,
          url: photo.urls.regular,
          thumb: photo.urls.thumb,
          description: photo.description || photo.alt_description,
          photographer: photo.user.name,
          photographerUrl: photo.user.links.html
        }))
      }));
    } catch (error) {
      console.error('Unsplash search error:', error);

      let errorMessage = 'Using demo images instead.';
      if (error.message.includes('Invalid')) {
        errorMessage = 'Invalid API key. Using demo images instead.';
      } else if (error.message.includes('rate limit')) {
        errorMessage = 'API rate limit reached. Using demo images instead.';
      }

      // Show a more user-friendly message
      console.log(`Unsplash search failed: ${errorMessage}`);

      // Fallback to demo images
      setUnsplashResults((prev) => ({
        ...prev,
        [section]: generateDemoImages(query)
      }));
    } finally {
      setUnsplashSearching((prev) => ({ ...prev, [section]: false }));
    }
  };

  // Generate demo images when Unsplash is unavailable
  const generateDemoImages = (query) => {
    const demoImages = [
      { id: '1', url: `https://picsum.photos/800/600?random=1&${query}`, thumb: `https://picsum.photos/200/150?random=1&${query}`, description: `Demo image for ${query}`, photographer: 'Demo', photographerUrl: '#' },
      { id: '2', url: `https://picsum.photos/800/600?random=2&${query}`, thumb: `https://picsum.photos/200/150?random=2&${query}`, description: `Demo image for ${query}`, photographer: 'Demo', photographerUrl: '#' },
      { id: '3', url: `https://picsum.photos/800/600?random=3&${query}`, thumb: `https://picsum.photos/200/150?random=3&${query}`, description: `Demo image for ${query}`, photographer: 'Demo', photographerUrl: '#' },
      { id: '4', url: `https://picsum.photos/800/600?random=4&${query}`, thumb: `https://picsum.photos/200/150?random=4&${query}`, description: `Demo image for ${query}`, photographer: 'Demo', photographerUrl: '#' }
    ];
    return demoImages;
  };

  // Handle file upload
  const handleImageUpload = async (section, file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageUrl = e.target.result;

      // Create image object
      const uploadedImage = {
        id: Date.now().toString(),
        url: imageUrl,
        thumb: imageUrl,
        description: file.name,
        photographer: 'Uploaded',
        photographerUrl: '#',
        uploaded: true
      };

      // Add to generated images
      setGeneratedImages((prev) => ({
        ...prev,
        [section]: [uploadedImage]
      }));

      // Close the selection mode
      setImageSelectionMode((prev) => ({ ...prev, [section]: null }));
    };

    reader.readAsDataURL(file);
  };

  // Handle URL input
  const handleImageFromUrl = async (section, url) => {
    if (!url || !url.startsWith('http')) {
      alert('Please enter a valid image URL');
      return;
    }

    // Create image object
    const urlImage = {
      id: Date.now().toString(),
      url: url,
      thumb: url,
      description: 'Image from URL',
      photographer: 'Custom',
      photographerUrl: '#',
      fromUrl: true
    };

    // Add to generated images
    setGeneratedImages((prev) => ({
      ...prev,
      [section]: [urlImage]
    }));

    // Close the selection mode
    setImageSelectionMode((prev) => ({ ...prev, [section]: null }));
  };

  // Select image from Unsplash results
  const selectUnsplashImage = async (section, image) => {
    // Add to generated images
    setGeneratedImages((prev) => ({
      ...prev,
      [section]: [image]
    }));

    // Close the selection mode
    setImageSelectionMode((prev) => ({ ...prev, [section]: null }));
  };

  const handleInputChange = (field, value) => {
    setProductInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Test data function to quickly populate the form
  const testDataOptions = {
    hydrosmart: {
      name: "HydroSmart Bottle",
      category: "Technology",
      keyProblem: "People forget to stay hydrated throughout the day, leading to dehydration and health issues",
      description: "A smart water bottle that tracks hydration levels, sends reminders, and syncs with health apps",
      targetAudience: "Health-conscious individuals, fitness enthusiasts, and busy professionals",
      fundingGoal: "$50,000"
    },
    aiheadphones: {
      name: "NeuroSound Pro",
      category: "Technology",
      keyProblem: "Noisy environments make it impossible to focus or enjoy music with clarity",
      description: "AI-powered noise-canceling headphones that adapt to your environment in real-time",
      targetAudience: "Remote workers, audiophiles, and frequent travelers",
      fundingGoal: "$100,000"
    },
    smartwatch: {
      name: "HealthSync Elite",
      category: "Technology",
      keyProblem: "Current fitness trackers provide too much data without actionable insights",
      description: "A smart watch that uses AI to provide personalized health recommendations and coaching",
      targetAudience: "Fitness enthusiasts and health-conscious professionals",
      fundingGoal: "$75,000"
    },
    ecobag: {
      name: "EcoCarry Pro",
      category: "Design",
      keyProblem: "Single-use bags are harming the environment and people need convenient alternatives",
      description: "A collapsible, ultra-durable shopping bag made from recycled materials with smart organization",
      targetAudience: "Environmentally conscious shoppers and urban dwellers",
      fundingGoal: "$25,000"
    }
  };

  const fillTestData = (dataKey = 'hydrosmart') => {
    setProductInfo(testDataOptions[dataKey]);
  };

  // Clear form function
  const clearForm = () => {
    setProductInfo({
      name: "",
      category: "",
      description: "",
      targetAudience: "",
      fundingGoal: "",
      keyProblem: "",
    });
  };

  const handlePromptChange = (section, newPrompt) => {
    setEditablePrompts((prev) => ({
      ...prev,
      [section]: newPrompt,
    }));
  };

  const loadProject = (project) => {
    setProductInfo(project.productInfo);
    setGeneratedContent(project.content);
    setCurrentProjectId(project.id);
    setCurrentStep("review");

    // Initialize editable prompts
    const prompts = {};
    if (project.content.overview?.imagePrompt) {
      prompts.overview = project.content.overview.imagePrompt;
    }
    project.content.features?.forEach(feature => {
      prompts[`feature-${feature.id}`] = feature.imagePrompt;
    });
    project.content.useCases?.forEach(useCase => {
      prompts[`usecase-${useCase.id}`] = useCase.imagePrompt;
    });
    project.content.rewards?.forEach(reward => {
      prompts[`reward-${reward.id}`] = reward.imagePrompt;
    });
    setEditablePrompts(prompts);
  };

  const deleteProject = async (projectId) => {
    try {
      // Delete from KV storage
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}?userId=${userId || 'anonymous'}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        console.log('Project deleted from KV storage');
      } else {
        console.warn('Failed to delete from KV storage');
      }
    } catch (error) {
      console.warn('Error deleting from KV storage:', error);
    }

    // Update local state and localStorage
    const updatedProjects = savedProjects.filter(p => p.id !== projectId);
    setSavedProjects(updatedProjects);
    localStorage.setItem('savedProjects', JSON.stringify(updatedProjects));

    if (currentProjectId === projectId) {
      setCurrentProjectId(null);
      setGeneratedContent(null);
      setCurrentStep("input");
    }
  };

  const startNewProject = () => {
    setProductInfo({
      name: "",
      category: "",
      description: "",
      targetAudience: "",
      fundingGoal: "",
      keyProblem: "",
    });
    setGeneratedContent(null);
    setCurrentProjectId(null);
    setCurrentStep("input");
    setGeneratedImages({});
    setEditablePrompts({});
  };

  // Export functions
  const copyContentToClipboard = async () => {
    if (!generatedContent) return;

    const textContent = `
# ${generatedContent.overview.title}

## ${generatedContent.overview.tagline}

${generatedContent.overview.mainDescription}

**Funding Goal:** ${generatedContent.overview.fundingGoal}

## Features

${generatedContent.features?.map(feature => `
### ${feature.title}
${feature.description}
**Benefit:** ${feature.benefit}
`).join('\n') || ''}

## Use Cases

${generatedContent.useCases?.map(useCase => `
### ${useCase.scenario} (${useCase.user})
${useCase.description}
`).join('\n') || ''}

## Reward Tiers

${generatedContent.rewards?.map(reward => `
### ${reward.tier} - ${reward.price}
${reward.description}
**Includes:** ${reward.includes?.join(', ') || ''}
**Delivery:** ${reward.delivery || ''}
`).join('\n') || ''}

## Timeline

${generatedContent.timeline?.map(phase => `
### ${phase.phase} (${phase.date})
${phase.description}
`).join('\n') || ''}

## FAQs

${generatedContent.faqs?.map(faq => `
**Q: ${faq.question}**
A: ${faq.answer}
`).join('\n') || ''}
    `.trim();

    try {
      await navigator.clipboard.writeText(textContent);
      alert('Content copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy content. Please try again.');
    }
  };

  const downloadImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download image:', err);
      alert(`Failed to download ${filename}`);
    }
  };

  const downloadAllImages = async () => {
    const allImages = [];

    // Collect all generated images
    Object.entries(generatedImages).forEach(([section, images]) => {
      if (Array.isArray(images)) {
        images.forEach((image, index) => {
          allImages.push({
            url: image.url,
            filename: `${section}-${index + 1}.png`
          });
        });
      }
    });

    if (allImages.length === 0) {
      alert('No images to download. Generate some images first!');
      return;
    }

    // Download each image
    for (const image of allImages) {
      await downloadImage(image.url, image.filename);
      // Small delay to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    alert(`Downloaded ${allImages.length} images!`);
  };

  const openWebsiteView = () => {
    // Create a new window with the campaign content
    const newWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!newWindow) {
      alert('Please allow popups to view the website version');
      return;
    }

    const websiteHTML = generateWebsiteHTML();
    newWindow.document.write(websiteHTML);
    newWindow.document.close();
  };

  const generateWebsiteHTML = () => {
    if (!generatedContent) return '';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${generatedContent.overview.title}</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
    </style>
</head>
<body class="bg-gray-50">
    <div class="max-w-4xl mx-auto py-8 px-4">
        <!-- Header -->
        <div class="text-center mb-12">
            <h1 class="text-5xl font-bold text-gray-900 mb-4">${generatedContent.overview.title}</h1>
            <p class="text-2xl text-purple-600 font-medium mb-6">${generatedContent.overview.tagline}</p>

            ${generatedImages.overview ? `
                <div class="mb-8">
                    <img src="${generatedImages.overview[0]?.url}" alt="Hero Image" class="mx-auto rounded-xl shadow-lg max-w-2xl w-full">
                </div>
            ` : ''}

            <p class="text-lg text-gray-700 leading-relaxed mb-8">${generatedContent.overview.mainDescription}</p>
            <div class="bg-green-100 text-green-800 px-6 py-3 rounded-full text-xl font-semibold inline-block">
                Funding Goal: ${generatedContent.overview.fundingGoal}
            </div>
        </div>

        <!-- Features -->
        <section class="mb-12">
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Features</h2>
            <div class="grid gap-6">
                ${generatedContent.features?.map(feature => `
                    <div class="bg-white p-6 rounded-xl shadow-lg">
                        ${generatedImages[`feature-${feature.id}`] ? `
                            <div class="mb-4">
                                <img src="${generatedImages[`feature-${feature.id}`][0]?.url}" alt="${feature.title}" class="w-full h-48 object-cover rounded-lg">
                            </div>
                        ` : ''}
                        <h3 class="text-xl font-semibold text-gray-800 mb-3">${feature.title}</h3>
                        <p class="text-gray-700 mb-4">${feature.description}</p>
                        <div class="bg-green-50 p-3 rounded-lg">
                            <p class="text-green-800 font-medium">✓ ${feature.benefit}</p>
                        </div>
                    </div>
                `).join('') || ''}
            </div>
        </section>

        <!-- Use Cases -->
        <section class="mb-12">
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Use Cases</h2>
            <div class="grid gap-6">
                ${generatedContent.useCases?.map(useCase => `
                    <div class="bg-white p-6 rounded-xl shadow-lg">
                        ${generatedImages[`usecase-${useCase.id}`] ? `
                            <div class="mb-4">
                                <img src="${generatedImages[`usecase-${useCase.id}`][0]?.url}" alt="${useCase.scenario}" class="w-full h-48 object-cover rounded-lg">
                            </div>
                        ` : ''}
                        <div class="flex items-center gap-3 mb-3">
                            <h3 class="text-xl font-semibold text-gray-800">${useCase.scenario}</h3>
                            <span class="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">${useCase.user}</span>
                        </div>
                        <p class="text-gray-700">${useCase.description}</p>
                    </div>
                `).join('') || ''}
            </div>
        </section>

        <!-- Rewards -->
        <section class="mb-12">
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Reward Tiers</h2>
            <div class="grid gap-6">
                ${generatedContent.rewards?.map(reward => `
                    <div class="bg-white p-6 rounded-xl shadow-lg border-2 border-purple-200">
                        ${generatedImages[`reward-${reward.id}`] ? `
                            <div class="mb-4">
                                <img src="${generatedImages[`reward-${reward.id}`][0]?.url}" alt="${reward.tier}" class="w-full h-48 object-cover rounded-lg">
                            </div>
                        ` : ''}
                        <div class="flex items-center gap-3 mb-4">
                            <h3 class="text-xl font-semibold text-gray-800">${reward.tier}</h3>
                            <span class="text-2xl font-bold text-purple-600">${reward.price}</span>
                            ${reward.originalPrice ? `<span class="text-lg text-gray-500 line-through">${reward.originalPrice}</span>` : ''}
                        </div>
                        <p class="text-gray-700 mb-4">${reward.description}</p>
                        ${reward.includes ? `
                            <div class="mb-4">
                                <p class="font-medium text-gray-600 mb-2">Includes:</p>
                                <ul class="text-sm text-gray-700 space-y-1">
                                    ${reward.includes.map(item => `<li>• ${item}</li>`).join('')}
                                </ul>
                            </div>
                        ` : ''}
                        ${reward.delivery ? `<p class="text-sm text-gray-600">Delivery: ${reward.delivery}</p>` : ''}
                    </div>
                `).join('') || ''}
            </div>
        </section>

        <!-- Timeline -->
        <section class="mb-12">
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Timeline</h2>
            <div class="space-y-4">
                ${generatedContent.timeline?.map(phase => `
                    <div class="bg-white p-6 rounded-xl shadow-lg">
                        <div class="flex items-center gap-3 mb-2">
                            <h3 class="text-lg font-semibold text-gray-800">${phase.phase}</h3>
                            <span class="text-sm text-gray-600">${phase.date}</span>
                            <span class="text-xs px-2 py-1 rounded-full font-medium ${phase.status === 'completed' ? 'bg-green-100 text-green-800' :
        phase.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
      }">
                            ${phase.status.replace('-', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p class="text-gray-700 leading-relaxed">
                          ${phase.description}
                        </p>
                      </div>
                    </div>
                  `).join('') || ''}
            </div>
        </section>

        <!-- FAQs -->
        <section class="mb-12">
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div class="space-y-6">
                ${generatedContent.faqs?.map(faq => `
                    <div class="bg-white p-6 rounded-xl shadow-lg">
                        <h3 class="text-lg font-semibold text-gray-800 mb-3">${faq.question}</h3>
                        <p class="text-gray-700">${faq.answer}</p>
                    </div>
                `).join('') || ''}
            </div>
        </section>

        <!-- Footer -->
        <div class="text-center py-8 border-t border-gray-200">
            <p class="text-gray-600">Generated with LaunchPad.App 🚀</p>
        </div>
    </div>

    ${campaignSettings.showLeadCapture ? `
    <!-- Lead Capture Bar -->
    <div id="leadCaptureBar" class="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 shadow-lg z-50">
        <div class="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div class="flex items-center gap-4">
                <div class="text-center md:text-left">
                    <h4 class="font-bold text-lg">
                        🚀 ${generatedContent.overview.title}
                    </h4>
                    <p class="text-sm opacity-90">
                        ${campaignSettings.promotionalMessage}
                    </p>
                </div>

                ${campaignSettings.launchDate ? `
                <div class="bg-white/20 px-4 py-2 rounded-lg text-center">
                    <div class="text-xs opacity-75">Campaign Launches In:</div>
                    <div id="countdown" class="font-mono font-bold">00:00:00</div>
                </div>
                ` : ''}
            </div>

            <div class="flex items-center gap-3">
                <form id="emailForm" class="flex gap-2">
                    <input
                        type="email"
                        id="emailInput"
                        placeholder="Enter your email..."
                        class="px-4 py-2 rounded-lg text-gray-800 border-0 focus:ring-2 focus:ring-white/50 min-w-[250px]"
                        required
                    />
                    <button
                        type="submit"
                        class="bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg font-semibold transition-all"
                    >
                        ${campaignSettings.ctaText}
                    </button>
                </form>

                <button
                    onclick="document.getElementById('leadCaptureBar').style.display='none'"
                    class="text-white/70 hover:text-white text-xl font-bold ml-2"
                >
                    ×
                </button>
            </div>
        </div>
    </div>

    <script>
        ${campaignSettings.launchDate ? `
        // Countdown timer to launch date
        function updateCountdown() {
            const endTime = new Date('${campaignSettings.launchDate}').getTime();
            const now = new Date().getTime();
            const timeLeft = endTime - now;

            if (timeLeft > 0) {
                const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

                if (days > 0) {
                    document.getElementById('countdown').textContent =
                        days + 'd ' + String(hours).padStart(2, '0') + 'h ' + String(minutes).padStart(2, '0') + 'm';
                } else {
                    document.getElementById('countdown').textContent =
                        String(hours).padStart(2, '0') + ':' +
                        String(minutes).padStart(2, '0') + ':' +
                        String(seconds).padStart(2, '0');
                }
            } else {
                document.getElementById('countdown').textContent = 'LAUNCHED!';
            }
        }

        // Update countdown every second
        setInterval(updateCountdown, 1000);
        updateCountdown();
        ` : ''}

        // Handle email form submission
        document.getElementById('emailForm').addEventListener('submit', function(e) {
            e.preventDefault();
            const email = document.getElementById('emailInput').value;

            // Send to configured email service
            ${campaignSettings.emailService !== 'none' ? `
            fetch('${API_BASE_URL}/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email,
                    projectId: '${currentProjectId}',
                    userId: '${userId || 'anonymous'}',
                    webhookUrl: '${campaignSettings.emailService === 'webhook' ? campaignSettings.emailServiceConfig : ''}',
                    service: '${campaignSettings.emailService}',
                    config: '${campaignSettings.emailServiceConfig}',
                    campaign: '${generatedContent.overview.title}'
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    alert('🎉 Thanks for signing up! You\\'ll be notified when we launch.');
                    document.getElementById('leadCaptureBar').style.display = 'none';
                } else {
                    alert('Something went wrong. Please try again.');
                }
            })
            .catch(error => {
                console.error('Error:', error);
                alert('Something went wrong. Please try again.');
            });
            ` : `
            // No email service configured - just show success
            alert('🎉 Thanks for your interest! Email: ' + email);
            document.getElementById('leadCaptureBar').style.display = 'none';
            `}
        });
    </script>
    ` : ''}

</body>
</html>
    `;
  };

  // Image Selection Component
  const renderImageSelection = (section, prompt) => {
    const mode = imageSelectionMode[section];

    if (!mode) {
      return (
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={() => generateImagesForSection(section)}
            disabled={imageGenerating[section]}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50"
          >
            {imageGenerating[section] ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                AI Generate
              </>
            )}
          </button>

          <button
            onClick={() => setImageSelectionMode(prev => ({ ...prev, [section]: 'unsplash' }))}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all"
          >
            <Camera className="w-4 h-4" />
            Search Unsplash
          </button>

          <button
            onClick={() => setImageSelectionMode(prev => ({ ...prev, [section]: 'upload' }))}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
          >
            <Upload className="w-4 h-4" />
            Upload Image
          </button>

          <button
            onClick={() => setImageSelectionMode(prev => ({ ...prev, [section]: 'url' }))}
            className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-all"
          >
            <Link className="w-4 h-4" />
            Use URL
          </button>
        </div>
      );
    }

    // Unsplash Search Mode
    if (mode === 'unsplash') {
      return (
        <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-800">Search Unsplash</h4>
            <button
              onClick={() => setImageSelectionMode(prev => ({ ...prev, [section]: null }))}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {(!process.env.REACT_APP_UNSPLASH_ACCESS_KEY || process.env.REACT_APP_UNSPLASH_ACCESS_KEY === 'demo-key') && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Demo Mode:</strong> Unsplash API not configured. Showing demo images instead.
                <br />
                <span className="text-xs">To use real Unsplash photos, add your API key to .env file.</span>
              </p>
            </div>
          )}

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Search for images..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchUnsplash(section, e.target.value);
                }
              }}
            />
            <button
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                searchUnsplash(section, input.value);
              }}
              disabled={unsplashSearching[section]}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {unsplashSearching[section] ? 'Searching...' : 'Search'}
            </button>
          </div>

          {unsplashResults[section] && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {unsplashResults[section].map((image) => (
                <div
                  key={image.id}
                  className="cursor-pointer group relative overflow-hidden rounded-lg"
                  onClick={() => selectUnsplashImage(section, image)}
                >
                  <img
                    src={image.thumb}
                    alt={image.description}
                    className="w-full h-24 object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium">
                      Select
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Upload Mode
    if (mode === 'upload') {
      return (
        <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-800">Upload Image</h4>
            <button
              onClick={() => setImageSelectionMode(prev => ({ ...prev, [section]: null }))}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleImageUpload(section, e.target.files[0]);
                }
              }}
              className="hidden"
              id={`upload-${section}`}
            />
            <label
              htmlFor={`upload-${section}`}
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="text-gray-600">Click to select an image file</span>
              <span className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</span>
            </label>
          </div>
        </div>
      );
    }

    // URL Mode
    if (mode === 'url') {
      return (
        <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-800">Use Image URL</h4>
            <button
              onClick={() => setImageSelectionMode(prev => ({ ...prev, [section]: null }))}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="flex gap-2">
            <input
              type="url"
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleImageFromUrl(section, e.target.value);
                }
              }}
            />
            <button
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                handleImageFromUrl(section, input.value);
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Use Image
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderContentSection = () => {
    if (!generatedContent) return null;

    console.log("Rendering section:", selectedSection);
    console.log("Available content:", {
      overview: !!generatedContent.overview,
      features: generatedContent.features?.length || 0,
      useCases: generatedContent.useCases?.length || 0,
      rewards: generatedContent.rewards?.length || 0
    });

    switch (selectedSection) {
      case "overview":
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {generatedContent.overview.title}
              </h3>
              <p className="text-lg text-purple-600 font-medium mb-4">
                {generatedContent.overview.tagline}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                {generatedContent.overview.mainDescription}
              </p>
              <div className="flex items-center gap-4">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                  Goal: {generatedContent.overview.fundingGoal}
                </span>
              </div>

              {/* Image Selection Component */}
              {renderImageSelection("overview", generatedContent.overview.imagePrompt)}
              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600 font-medium mb-2">
                  Image Prompt (editable):
                </p>
                <textarea
                  value={editablePrompts.overview || generatedContent.overview.imagePrompt}
                  onChange={(e) => handlePromptChange("overview", e.target.value)}
                  className="w-full text-sm text-gray-700 border border-gray-300 rounded p-2 resize-none"
                  rows={3}
                  placeholder="Edit the image prompt here..."
                />
              </div>

              {/* Generated Images Display */}
              {generatedImages.overview && (
                <div className="mt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">
                    Generated Hero Image:
                  </h4>
                  <div className="grid gap-4">
                    {generatedImages.overview.map((image, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                      >
                        <img
                          src={image.url}
                          alt={image.title}
                          className="w-full h-64 object-cover"
                        />
                        <div className="p-3">
                          <p className="text-sm text-gray-600 font-medium">
                            Generated for: {image.title}
                          </p>
                          <button
                            onClick={() => window.open(image.url, "_blank")}
                            className="mt-2 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded transition-all"
                          >
                            View Full Size
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "features":
        return (
          <div className="space-y-6">
            {generatedContent.features.map((feature) => (
              <div
                key={feature.id}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-xl font-semibold text-gray-800">
                    {feature.title}
                  </h4>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  {feature.description}
                </p>
                <div className="bg-green-50 p-3 rounded-lg mb-4">
                  <p className="text-green-800 font-medium text-sm">
                    ✓ {feature.benefit}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <p className="text-xs text-gray-600 font-medium mb-2">
                    Image Prompt (editable):
                  </p>
                  <textarea
                    value={editablePrompts[`feature-${feature.id}`] || feature.imagePrompt}
                    onChange={(e) => handlePromptChange(`feature-${feature.id}`, e.target.value)}
                    className="w-full text-xs text-gray-700 border border-gray-300 rounded p-2 resize-none"
                    rows={2}
                    placeholder="Edit the image prompt here..."
                  />
                </div>

                {/* Image Selection Component */}
                {renderImageSelection(`feature-${feature.id}`, feature.imagePrompt)}

                {/* Generated Images for Feature */}
                {generatedImages[`feature-${feature.id}`] && (
                  <div className="mt-4">
                    <div className="grid gap-3">
                      {generatedImages[`feature-${feature.id}`].map(
                        (image, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                          >
                            <img
                              src={image.url}
                              alt={image.title}
                              className="w-full h-48 object-cover"
                            />
                            <div className="p-2">
                              <button
                                onClick={() => window.open(image.url, "_blank")}
                                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-all"
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case "usecases":
        return (
          <div className="space-y-6">
            {generatedContent.useCases?.map((useCase) => (
              <div
                key={useCase.id}
                className="bg-white p-6 rounded-xl border border-gray-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800">
                      {useCase.scenario}
                    </h4>
                    <span className="inline-block bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full mt-1">
                      {useCase.user}
                    </span>
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {useCase.description}
                </p>
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <p className="text-xs text-gray-600 font-medium mb-2">
                    Image Prompt (editable):
                  </p>
                  <textarea
                    value={editablePrompts[`usecase-${useCase.id}`] || useCase.imagePrompt}
                    onChange={(e) => handlePromptChange(`usecase-${useCase.id}`, e.target.value)}
                    className="w-full text-xs text-gray-700 border border-gray-300 rounded p-2 resize-none"
                    rows={2}
                    placeholder="Edit the image prompt here..."
                  />
                </div>

                {/* Image Selection Component */}
                {renderImageSelection(`usecase-${useCase.id}`, useCase.imagePrompt)}

                {/* Generated Images for Use Case */}
                {generatedImages[`usecase-${useCase.id}`] && (
                  <div className="mt-4">
                    <div className="grid gap-3">
                      {generatedImages[`usecase-${useCase.id}`].map(
                        (image, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                          >
                            <img
                              src={image.url}
                              alt={image.title}
                              className="w-full h-48 object-cover"
                            />
                            <div className="p-2">
                              <button
                                onClick={() => window.open(image.url, "_blank")}
                                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-all"
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )) || <div>No use cases generated</div>}
          </div>
        );

      case "rewards":
        return (
          <div className="space-y-6">
            {generatedContent.rewards?.map((reward) => (
              <div
                key={reward.id}
                className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800">
                      {reward.tier}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl font-bold text-purple-600">
                        {reward.price}
                      </span>
                      {reward.originalPrice && (
                        <>
                          <span className="text-lg text-gray-500 line-through">
                            {reward.originalPrice}
                          </span>
                          <span className="bg-red-100 text-red-800 text-sm px-2 py-1 rounded-full">
                            {Math.round(
                              (1 -
                                parseInt(reward.price.replace("$", "")) /
                                parseInt(reward.originalPrice.replace("$", ""))) *
                              100
                            )}
                            % OFF
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {reward.description}
                </p>
                {reward.includes && (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Includes:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {reward.includes.map((item, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Details:
                      </p>
                      {reward.delivery && (
                        <p className="text-sm text-gray-700 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Delivery: {reward.delivery}
                        </p>
                      )}
                      {reward.limited && (
                        <p className="text-sm text-orange-600 font-medium">
                          {reward.limited}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <p className="text-xs text-gray-600 font-medium mb-2">
                    Image Prompt (editable):
                  </p>
                  <textarea
                    value={editablePrompts[`reward-${reward.id}`] || reward.imagePrompt}
                    onChange={(e) => handlePromptChange(`reward-${reward.id}`, e.target.value)}
                    className="w-full text-xs text-gray-700 border border-gray-300 rounded p-2 resize-none"
                    rows={2}
                    placeholder="Edit the image prompt here..."
                  />
                </div>

                {/* Image Selection Component */}
                {renderImageSelection(`reward-${reward.id}`, reward.imagePrompt)}

                {/* Generated Images for Reward */}
                {generatedImages[`reward-${reward.id}`] && (
                  <div className="mt-4">
                    <div className="grid gap-3">
                      {generatedImages[`reward-${reward.id}`].map(
                        (image, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                          >
                            <img
                              src={image.url}
                              alt={image.title}
                              className="w-full h-48 object-cover"
                            />
                            <div className="p-2">
                              <button
                                onClick={() => window.open(image.url, "_blank")}
                                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-all"
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )) || <div>No rewards generated</div>}
          </div>
        );

      case "timeline":
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Project Timeline
              </h3>
              <p className="text-gray-600">
                Our roadmap from concept to delivery
              </p>
            </div>
            {generatedContent.timeline?.map((phase, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${phase.status === 'completed' ? 'bg-green-500' :
                      phase.status === 'in-progress' ? 'bg-blue-500' : 'bg-gray-400'
                      }`}>
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-xl font-semibold text-gray-800">
                        {phase.phase}
                      </h4>
                      <span className="text-sm text-gray-600">{phase.date}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${phase.status === 'completed' ? 'bg-green-100 text-green-800' :
                        phase.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                        {phase.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {phase.description}
                    </p>
                  </div>
                </div>
              </div>
            )) || <div>No timeline generated</div>}
          </div>
        );

      case "faqs":
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Frequently Asked Questions
              </h3>
              <p className="text-gray-600">
                Everything you need to know about our project
              </p>
            </div>
            {generatedContent.faqs?.map((faq, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all"
              >
                <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-start gap-2">
                  <span className="text-purple-600 font-bold">Q:</span>
                  {faq.question}
                </h4>
                <p className="text-gray-700 leading-relaxed pl-6">
                  <span className="text-green-600 font-bold">A:</span> {faq.answer}
                </p>
              </div>
            )) || <div>No FAQs generated</div>}
          </div>
        );

      case "export":
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Export & Share Your Campaign
              </h3>
              <p className="text-gray-600 mb-8">
                Share your campaign or download all content and images
              </p>
            </div>

            {/* Export Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* View as Website */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-6 h-6 text-blue-600" />
                  <h4 className="text-lg font-semibold text-gray-800">
                    View as Website
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  Open your campaign in a new window as a beautiful, shareable website
                </p>
                <button
                  onClick={openWebsiteView}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Open Website View
                </button>
              </div>

              {/* Complete Package */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
                <div className="flex items-center gap-3 mb-4">
                  <FolderDown className="w-6 h-6 text-orange-600" />
                  <h4 className="text-lg font-semibold text-gray-800">
                    Publish & Download
                  </h4>
                </div>
                <p className="text-gray-600 mb-6">
                  Publish your campaign online or download all files locally
                </p>

                <div className="space-y-3">
                  {/* Publish Website Button */}
                  <button
                    onClick={async () => {
                      try {
                        // Create complete package in R2
                        const htmlContent = generateWebsiteHTML();
                        const allImages = [];

                        // Collect all generated images
                        Object.entries(generatedImages).forEach(([section, images]) => {
                          if (Array.isArray(images)) {
                            images.forEach((image, index) => {
                              allImages.push({
                                url: image.url,
                                filename: `${section}-${index + 1}.png`
                              });
                            });
                          }
                        });

                        const packageResponse = await fetch(`${API_BASE_URL}/api/create-package`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            projectId: currentProjectId || Date.now().toString(),
                            htmlContent: htmlContent,
                            images: allImages
                          })
                        });

                        const packageData = await packageResponse.json();

                        if (packageData.success) {
                          // Save the generated website URL
                          setGeneratedWebsiteUrl(packageData.manifest.packageUrl);

                          // Open the hosted campaign page
                          window.open(packageData.manifest.packageUrl, '_blank');

                          // Show success message
                          alert('🎉 Website published! Your campaign is now live and can be shared with the URL displayed below.');
                        } else {
                          throw new Error(packageData.error);
                        }
                      } catch (error) {
                        console.error('Package creation error:', error);
                        alert('Failed to publish website. Please try again or use the download option.');
                      }
                    }}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Publish Website Online
                  </button>

                  {/* Download Files Button */}
                  <button
                    onClick={async () => {
                      try {
                        // Download HTML file
                        const htmlContent = generateWebsiteHTML();
                        const blob = new Blob([htmlContent], { type: 'text/html' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${generatedContent.overview.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_campaign.html`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                        // Download all images after a short delay
                        setTimeout(() => downloadAllImages(), 1000);

                        alert('📁 Files are downloading! Check your Downloads folder for the HTML file and images.');
                      } catch (error) {
                        console.error('Download error:', error);
                        alert('Failed to download files. Please try again.');
                      }
                    }}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download All Files
                  </button>
                </div>
              </div>

              {/* Copy Content */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-center gap-3 mb-4">
                  <Copy className="w-6 h-6 text-green-600" />
                  <h4 className="text-lg font-semibold text-gray-800">
                    Copy Content
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  Copy all campaign text to your clipboard for easy pasting
                </p>
                <button
                  onClick={copyContentToClipboard}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy All Text
                </button>
              </div>

              {/* Download Images */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <Download className="w-6 h-6 text-purple-600" />
                  <h4 className="text-lg font-semibold text-gray-800">
                    Download Images
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  Download all generated images to your computer
                </p>
                <button
                  onClick={downloadAllImages}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                  disabled={Object.keys(generatedImages).length === 0}
                >
                  <Download className="w-4 h-4" />
                  Download All Images
                </button>
                {Object.keys(generatedImages).length === 0 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Generate some images first
                  </p>
                )}
              </div>


            </div>

            {/* Generated Website URL */}
            {generatedWebsiteUrl && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border border-green-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Live Campaign Website
                </h4>
                <p className="text-gray-600 mb-4">
                  Your campaign website is now live and ready to share!
                </p>
                <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-500">Website URL:</span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="text"
                      value={generatedWebsiteUrl}
                      readOnly
                      className="flex-1 p-2 text-sm bg-gray-50 border border-gray-200 rounded-md font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedWebsiteUrl);
                        alert('URL copied to clipboard!');
                      }}
                      className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => window.open(generatedWebsiteUrl, '_blank')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View Website
                    </button>
                    <button
                      onClick={() => {
                        const shareData = {
                          title: generatedContent.overview?.title || 'My Kickstarter Campaign',
                          text: 'Check out my new Kickstarter campaign!',
                          url: generatedWebsiteUrl
                        };
                        if (navigator.share) {
                          navigator.share(shareData);
                        } else {
                          // Fallback for browsers that don't support Web Share API
                          const text = `${shareData.title} - ${shareData.text} ${shareData.url}`;
                          navigator.clipboard.writeText(text);
                          alert('Share text copied to clipboard!');
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Statistics */}
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                Campaign Statistics
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {generatedContent.features?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Features</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {generatedContent.useCases?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Use Cases</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {generatedContent.rewards?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Reward Tiers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {Object.values(generatedImages).flat().length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Images Generated</div>
                </div>
              </div>
            </div>

            {/* Campaign Settings */}
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-6 rounded-xl border-2 border-blue-300">
              <h4 className="text-xl font-bold text-blue-800 mb-4">
                ⚙️ Campaign Settings
              </h4>
              <p className="text-blue-700 mb-6">
                Configure your campaign's lead capture settings for the shared website
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Launch Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Launch Date
                  </label>
                  <input
                    type="datetime-local"
                    value={campaignSettings.launchDate}
                    onChange={(e) => setCampaignSettings(prev => ({ ...prev, launchDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sets the countdown timer on your shared website
                  </p>
                </div>

                {/* Email Service */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Service
                  </label>
                  <select
                    value={campaignSettings.emailService}
                    onChange={(e) => setCampaignSettings(prev => ({ ...prev, emailService: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="none">No email capture</option>
                    <option value="mailchimp">Mailchimp</option>
                    <option value="convertkit">ConvertKit</option>
                    <option value="airtable">Airtable</option>
                    <option value="webhook">Custom Webhook</option>
                  </select>
                </div>

                {/* Email Service Config */}
                {campaignSettings.emailService !== 'none' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {campaignSettings.emailService === 'mailchimp' && 'Mailchimp API Key & List ID'}
                      {campaignSettings.emailService === 'convertkit' && 'ConvertKit API Key & Form ID'}
                      {campaignSettings.emailService === 'airtable' && 'Airtable Base ID & Table Name'}
                      {campaignSettings.emailService === 'webhook' && 'Webhook URL'}
                    </label>
                    <input
                      type="text"
                      value={campaignSettings.emailServiceConfig}
                      onChange={(e) => setCampaignSettings(prev => ({ ...prev, emailServiceConfig: e.target.value }))}
                      placeholder={
                        campaignSettings.emailService === 'mailchimp' ? 'api_key:list_id' :
                          campaignSettings.emailService === 'convertkit' ? 'api_key:form_id' :
                            campaignSettings.emailService === 'airtable' ? 'base_id:table_name' :
                              'https://your-webhook-url.com/subscribe'
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {campaignSettings.emailService === 'webhook' && (
                      <p className="mt-1 text-xs text-gray-500">
                        💡 Test with: <code className="bg-gray-100 px-1 rounded">https://httpbin.org/post</code>
                      </p>
                    )}

                    {/* Webhook Preview and Test */}
                    {campaignSettings.emailService === 'webhook' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-800">Webhook Payload Preview</h4>
                          {campaignSettings.emailServiceConfig && (
                            <button
                              onClick={async () => {
                                try {
                                  console.log('Testing webhook:', campaignSettings.emailServiceConfig);
                                  const response = await fetch(`${API_BASE_URL}/api/test-webhook`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                      webhookUrl: campaignSettings.emailServiceConfig,
                                      projectId: currentProjectId || "test-project-123"
                                    })
                                  });

                                  if (!response.ok) {
                                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                                  }

                                  const data = await response.json();
                                  console.log('Webhook test response:', data);

                                  if (data.success) {
                                    alert(`✅ Webhook test successful!\n\nStatus: ${data.status}\nResponse: ${data.response || 'No response body'}`);
                                  } else {
                                    alert(`❌ Webhook test failed: ${data.error}\n\nResponse: ${data.response || 'No additional details'}`);
                                  }
                                } catch (error) {
                                  console.error('Webhook test error:', error);
                                  alert(`❌ Webhook test failed: ${error.message}\n\nPlease check:\n- Webhook URL is accessible\n- Webhook accepts POST requests\n- No network restrictions`);
                                }
                              }}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                              Test Webhook
                            </button>
                          )}
                        </div>

                        <p className="text-xs text-gray-600 mb-2">
                          Your webhook will receive this JSON payload when someone subscribes:
                        </p>

                        <pre className="bg-white p-3 rounded border text-xs text-gray-800 overflow-x-auto">
                          {`{
  "event": "email_subscription",
  "data": {
    "email": "user@example.com",
    "projectId": "${currentProjectId || 'your-project-id'}",
    "subscribedAt": "${new Date().toISOString()}",
    "source": "launchpad-app-ai-generator"
  }
}`}
                        </pre>

                        <div className="mt-3 text-xs text-gray-500">
                          <p><strong>Headers:</strong> Content-Type: application/json, User-Agent: launchpad-app-ai-generator/1.0</p>
                          <p><strong>Method:</strong> POST</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Promotional Message */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Promotional Message
                  </label>
                  <textarea
                    value={campaignSettings.promotionalMessage}
                    onChange={(e) => setCampaignSettings(prev => ({ ...prev, promotionalMessage: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Get early access to our revolutionary product + 50% OFF launch pricing!"
                  />
                </div>

                {/* CTA Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call-to-Action Button Text
                  </label>
                  <input
                    type="text"
                    value={campaignSettings.ctaText}
                    onChange={(e) => setCampaignSettings(prev => ({ ...prev, ctaText: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Notify Me When Available"
                  />
                </div>

                {/* Show Lead Capture */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showLeadCapture"
                    checked={campaignSettings.showLeadCapture}
                    onChange={(e) => setCampaignSettings(prev => ({ ...prev, showLeadCapture: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showLeadCapture" className="ml-2 block text-sm text-gray-700">
                    Show lead capture bar on shared website
                  </label>
                </div>
              </div>
            </div>

            {/* Sharing Tips */}
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                💡 Sharing Tips
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Use the <strong>Website View</strong> to share a live preview with stakeholders</li>
                <li>• <strong>Copy the text</strong> to paste into Kickstarter's campaign editor</li>
                <li>• <strong>Download images</strong> to upload to your campaign page</li>
                <li>• Save the <strong>HTML file</strong> as a backup of your complete campaign</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (currentStep === "input") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Settings Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowKeysModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Configure API Keys"
            >
              <Settings className="w-5 h-5" />
              <span className="hidden sm:inline">Settings</span>
            </button>
          </div>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-8 h-8 text-purple-600" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                AI Campaign Generator
              </h1>
            </div>
            <p className="text-gray-600 text-lg">
              Create comprehensive Kickstarter campaigns with AI-generated
              content and images
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-purple-600" />
              Tell us about your product
            </h2>

            {/* AI Provider Selection */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                AI Content Generator
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="anthropic"
                    name="aiProvider"
                    value="anthropic"
                    checked={aiProvider === "anthropic"}
                    onChange={(e) => setAiProvider(e.target.value)}
                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500"
                  />
                  <label htmlFor="anthropic" className="ml-2 text-sm font-medium text-gray-700">
                    Claude (Anthropic) - Premium Quality
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="openai"
                    name="aiProvider"
                    value="openai"
                    checked={aiProvider === "openai"}
                    onChange={(e) => setAiProvider(e.target.value)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="openai" className="ml-2 text-sm font-medium text-gray-700">
                    GPT-4 (OpenAI) - Cost Effective
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {aiProvider === "anthropic" ?
                  "Claude provides detailed, nuanced content with excellent creative writing." :
                  "GPT-4 offers high-quality content at a lower cost with fast generation speed."
                }
              </p>
            </div>

            {/* Test Data Button */}
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Quick Test Data
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => fillTestData('hydrosmart')}
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  HydroSmart Bottle
                </button>
                <button
                  onClick={() => fillTestData('aiheadphones')}
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  AI Headphones
                </button>
                <button
                  onClick={() => fillTestData('smartwatch')}
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Smart Watch
                </button>
                <button
                  onClick={() => fillTestData('ecobag')}
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Eco Bag
                </button>
                <button
                  onClick={clearForm}
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Clear Form
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Click any button to instantly populate the form with realistic test data
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={productInfo.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Smart Fitness Tracker Pro"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={productInfo.category}
                  onChange={(e) =>
                    handleInputChange("category", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What problem does it solve? *
                </label>
                <textarea
                  value={productInfo.keyProblem}
                  onChange={(e) =>
                    handleInputChange("keyProblem", e.target.value)
                  }
                  placeholder="Traditional fitness trackers lack accurate sleep monitoring and personalized health insights..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Description
                </label>
                <textarea
                  value={productInfo.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="An advanced fitness tracker that uses AI to provide personalized health insights, accurate sleep monitoring, and predictive wellness recommendations..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  value={productInfo.targetAudience}
                  onChange={(e) =>
                    handleInputChange("targetAudience", e.target.value)
                  }
                  placeholder="Health-conscious professionals, fitness enthusiasts"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funding Goal
                </label>
                <input
                  type="text"
                  value={productInfo.fundingGoal}
                  onChange={(e) =>
                    handleInputChange("fundingGoal", e.target.value)
                  }
                  placeholder="$50,000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <button
              onClick={generateCampaignContent}
              disabled={
                !productInfo.name ||
                !productInfo.category ||
                !productInfo.keyProblem
              }
              className="w-full mt-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Wand2 className="w-5 h-5" />
              Generate Complete Campaign Content
            </button>
          </div>
        </div>

        {/* API Keys Modal */}
        <ApiKeysModal
          isOpen={showKeysModal}
          onClose={() => setShowKeysModal(false)}
          apiKeys={apiKeys}
          setApiKeys={setApiKeys}
          showKeyValues={showKeyValues}
          setShowKeyValues={setShowKeyValues}
        />
      </div>
    );
  }

  if (currentStep === "generating") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md">
          <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Creating Your Campaign
          </h2>
          <p className="text-gray-600 mb-6">
            Our AI is crafting comprehensive content for your Kickstarter
            campaign...
          </p>
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">
                Analyzing your product details
              </span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">
                Generating campaign description
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="text-sm text-gray-700">
                Creating feature highlights
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">
                Building reward tiers
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">
                Preparing image prompts
              </span>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-4">
              This usually takes 30-60 seconds. If it's taking longer, the AI service might be busy.
            </p>
            <button
              onClick={() => setCurrentStep("input")}
              className="text-sm text-purple-600 hover:text-purple-700 underline"
            >
              Cancel and try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === "review") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
              Your AI-Generated Campaign
            </h1>
            <p className="text-gray-600 text-lg">
              Review content and generate images for each section
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Saved Projects Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Saved Projects
                  </h3>
                  <button
                    onClick={startNewProject}
                    className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition-all"
                  >
                    + New
                  </button>
                </div>

                {/* User ID Display */}
                {userId && (
                  <div className="mb-4 p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">
                      User ID: <span className="font-mono text-gray-700">{userId}</span>
                    </p>
                  </div>
                )}

                <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                  {savedProjects.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No saved projects yet
                    </p>
                  ) : (
                    savedProjects.map((project) => (
                      <div
                        key={project.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${currentProjectId === project.id
                          ? "bg-purple-50 border-purple-300"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                          }`}
                      >
                        <div
                          onClick={() => loadProject(project)}
                          className="flex-1"
                        >
                          <h4 className="text-sm font-medium text-gray-800 truncate">
                            {project.name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {new Date(project.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this project?')) {
                              deleteProject(project.id);
                            }
                          }}
                          className="text-red-500 hover:text-red-700 text-xs mt-1"
                        >
                          Delete
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Campaign Sections
                </h3>

                {/* Image Generation Service Selector */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Image Generation Service
                  </h4>
                  <select
                    value={imageGenerationService}
                    onChange={(e) => setImageGenerationService(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="dalle">DALL-E 3 (AI Generated)</option>
                    <option value="templated">Templated.io (Professional Templates)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {imageGenerationService === "dalle"
                      ? "AI-generated unique images"
                      : "Professional template-based graphics"
                    }
                  </p>
                </div>
                <nav className="space-y-2">
                  {[
                    { id: "overview", label: "Overview", icon: FileText },
                    { id: "features", label: "Features", icon: Sparkles },
                    { id: "usecases", label: "Use Cases", icon: CheckCircle },
                    { id: "rewards", label: "Rewards", icon: Wand2 },
                    { id: "timeline", label: "Timeline", icon: Clock },
                    { id: "faqs", label: "FAQs", icon: CheckCircle },
                    { id: "export", label: "Export & Share", icon: Share2 },
                  ].map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setSelectedSection(section.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${selectedSection === section.id
                          ? "bg-purple-100 text-purple-700 border border-purple-300"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                          }`}
                      >
                        <Icon className="w-4 h-4" />
                        {section.label}
                        {selectedSection === section.id && (
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                {renderContentSection()}
                {renderImageSelection(selectedSection, editablePrompts[selectedSection])}
              </div>
            </div>
          </div>
        </div>


      </div>
    );
  }
};

export default KickstarterContentGenerator;
